import React, { useState, useEffect } from 'react';
import { ChevronDown, Brain, Dna, Activity, Sparkles, Calendar, LineChart, Zap, Check, X } from 'lucide-react';

const LEVWebsite = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [completedActions, setCompletedActions] = useState({});
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleAction = (id) => {
    setCompletedActions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // The 100 Actions - Organized by Priority Tiers
  const actionTiers = {
    "CRITICAL FOUNDATIONS (Do These First)": {
      color: "from-red-600 to-orange-600",
      icon: "🚨",
      description: "These are non-negotiable. Nothing else matters if you're actively harming yourself.",
      actions: [
        { id: 1, text: "Stop smoking immediately (including vaping, cannabis)", impact: "Removes single biggest accelerator of aging" },
        { id: 2, text: "Eliminate excessive alcohol consumption (>7 drinks/week)", impact: "Reduces DNA damage, liver stress, cancer risk" },
        { id: 3, text: "Achieve healthy sleep (7-9 hours nightly)", impact: "Enables cellular repair, immune function, cognitive maintenance" },
        { id: 4, text: "Avoid ultra-processed foods and added sugars", impact: "Prevents metabolic dysfunction, inflammation" },
        { id: 5, text: "Manage chronic stress (therapy, meditation, lifestyle changes)", impact: "Reduces cortisol-driven aging acceleration" },
        { id: 6, text: "Get annual comprehensive health screening", impact: "Early detection saves decades of life" },
        { id: 7, text: "Maintain healthy body composition (avoid obesity)", impact: "Reduces systemic inflammation, metabolic disease" },
        { id: 8, text: "Address mental health issues proactively", impact: "Mental and physical health deeply intertwined" },
        { id: 9, text: "Eliminate sedentary lifestyle (move daily)", impact: "Sitting is genuinely hazardous to longevity" },
        { id: 10, text: "Protect against preventable infections (vaccines current)", impact: "Immune system preservation is critical" }
      ]
    },
    "TIER 1: HIGH-EVIDENCE LIFESTYLE INTERVENTIONS": {
      color: "from-orange-500 to-yellow-500",
      icon: "⭐",
      description: "Strong scientific evidence. Maximum return on effort.",
      actions: [
        { id: 11, text: "Implement time-restricted eating (16:8 or similar)", impact: "Enhances autophagy, metabolic health" },
        { id: 12, text: "Resistance training 3-4x per week", impact: "Prevents sarcopenia, maintains metabolic rate" },
        { id: 13, text: "Cardiovascular exercise 150+ min/week", impact: "Heart health, VO2max preservation critical for longevity" },
        { id: 14, text: "Mediterranean diet or similar plant-forward approach", impact: "Best-studied longevity diet pattern" },
        { id: 15, text: "Optimize sleep quality (dark room, cool temp, consistent schedule)", impact: "Deep sleep enables growth hormone, cellular cleanup" },
        { id: 16, text: "Build and maintain strong social connections", impact: "Social isolation = smoking 15 cigarettes/day in mortality risk" },
        { id: 17, text: "Daily sun exposure (10-30 min) for Vitamin D", impact: "Immune function, bone health, mood regulation" },
        { id: 18, text: "Maintain cognitive engagement (learning, novelty)", impact: "Neuroplasticity preservation, dementia prevention" },
        { id: 19, text: "Practice stress reduction techniques daily", impact: "Telomere preservation, inflammatory control" },
        { id: 20, text: "Optimize hydration (2-3L water daily)", impact: "Cellular function, detoxification, organ health" }
      ]
    },
    "TIER 2: EVIDENCE-BASED SUPPLEMENTS": {
      color: "from-yellow-500 to-green-500",
      icon: "💊",
      description: "Only after lifestyle foundations are solid. Quality matters enormously.",
      actions: [
        { id: 21, text: "Vitamin D3 (2000-4000 IU daily if deficient)", impact: "Slows biological aging ~3 years per research" },
        { id: 22, text: "Omega-3 fatty acids (EPA/DHA 2-3g daily)", impact: "Anti-inflammatory, cardiovascular, brain health" },
        { id: 23, text: "Magnesium glycinate (300-400mg daily)", impact: "Sleep quality, muscle function, stress response" },
        { id: 24, text: "Vitamin K2 (MK-7, 100-200mcg)", impact: "Calcium regulation, cardiovascular health" },
        { id: 25, text: "Quality multivitamin for nutritional insurance", impact: "Covers micronutrient gaps in modern diet" },
        { id: 26, text: "Probiotics or fermented foods daily", impact: "Gut microbiome = second brain + immune system" },
        { id: 27, text: "NAD+ precursors (NMN or NR, 250-500mg)", impact: "Cellular energy, DNA repair (emerging evidence)" },
        { id: 28, text: "Urolithin A (500mg)", impact: "Mitochondrial quality control, shown in 2025 research" },
        { id: 29, text: "Creatine monohydrate (5g daily)", impact: "Muscle preservation, cognitive function, especially 40+" },
        { id: 30, text: "Zinc (15-30mg) and Selenium (200mcg)", impact: "Immune function, antioxidant systems" }
      ]
    },
    "TIER 3: ADVANCED OPTIMIZATION": {
      color: "from-green-500 to-blue-500",
      icon: "🔬",
      description: "For those who've mastered the basics and want to optimize further.",
      actions: [
        { id: 31, text: "Track biomarkers quarterly (bloodwork, inflammation, metabolic)", impact: "Data-driven personalization vs guessing" },
        { id: 32, text: "Optimize circadian rhythm (wake with sunrise, dim lights at night)", impact: "Hormonal optimization, sleep quality" },
        { id: 33, text: "Practice heat exposure (sauna 3-4x/week, 20min at 80°C)", impact: "Heat shock proteins, cardiovascular conditioning" },
        { id: 34, text: "Cold exposure protocol (cold showers, ice baths)", impact: "Brown fat activation, metabolic boost, resilience" },
        { id: 35, text: "Maintain optimal body temperature while sleeping (cool)", impact: "Deep sleep enhancement, growth hormone release" },
        { id: 36, text: "Implement strategic caloric restriction (5-10% reduction)", impact: "Activates longevity pathways if not undernourished" },
        { id: 37, text: "Cycle protein intake (higher on training days)", impact: "mTOR modulation for longevity" },
        { id: 38, text: "Monitor and optimize VO2 max annually", impact: "Single best predictor of cardiovascular longevity" },
        { id: 39, text: "Practice breath work (box breathing, Wim Hof)", impact: "Autonomic nervous system control, stress resilience" },
        { id: 40, text: "Optimize dental health (floss daily, regular cleanings)", impact: "Oral bacteria linked to systemic inflammation, heart disease" }
      ]
    },
    "TIER 4: CUTTING-EDGE INTERVENTIONS": {
      color: "from-blue-500 to-purple-500",
      icon: "🚀",
      description: "Emerging interventions with growing evidence. Consult physicians.",
      actions: [
        { id: 41, text: "Consider metformin (500-1000mg) if pre-diabetic or diabetic", impact: "Anti-aging effects shown in primates, human trials ongoing" },
        { id: 42, text: "Rapamycin microdosing (consult longevity physician)", impact: "mTOR inhibition, most robust life-extension drug in animals" },
        { id: 43, text: "Evaluate GLP-1 agonists if overweight/obese", impact: "6.4% mortality reduction projected by 2045" },
        { id: 44, text: "Get epigenetic age testing (TruAge, GrimAge)", impact: "Know your biological vs chronological age" },
        { id: 45, text: "Consider therapeutic testosterone if clinically low", impact: "Muscle mass, bone density, cognitive function preservation" },
        { id: 46, text: "Evaluate senolytic protocols (fisetin, quercetin cycles)", impact: "Removes zombie cells that accelerate aging" },
        { id: 47, text: "NAD+ IV therapy quarterly", impact: "Direct cellular energy boost (more potent than oral)" },
        { id: 48, text: "Hyperbaric oxygen therapy sessions", impact: "Telomere lengthening, stem cell mobilization" },
        { id: 49, text: "Red light therapy daily (near-infrared)", impact: "Mitochondrial function, skin health, inflammation" },
        { id: 50, text: "Peptide therapy under medical supervision", impact: "BPC-157, thymosin, epithalon for specific targets" }
      ]
    },
    "TIER 5: PRECISION & MONITORING": {
      color: "from-purple-500 to-pink-500",
      icon: "📊",
      description: "Know thyself. Data enables personalized optimization.",
      actions: [
        { id: 51, text: "Comprehensive genetic testing (23andMe, Promethease)", impact: "Understand personal risk factors, optimize accordingly" },
        { id: 52, text: "Continuous glucose monitoring (even if not diabetic)", impact: "Optimize diet for metabolic health in real-time" },
        { id: 53, text: "Track HRV (heart rate variability) daily", impact: "Autonomic nervous system health, recovery status" },
        { id: 54, text: "Monitor sleep with quality tracker (Oura, Whoop)", impact: "Optimize sleep quality, identify issues" },
        { id: 55, text: "Regular DEXA scans (body composition)", impact: "Track muscle mass, bone density, visceral fat" },
        { id: 56, text: "Advanced cardiovascular imaging (CAC score, CIMT)", impact: "Detect arterial aging before symptoms" },
        { id: 57, text: "Comprehensive microbiome analysis", impact: "Gut health = system health, personalize diet" },
        { id: 58, text: "Track biological age markers (methylation clocks)", impact: "Measure if interventions are actually working" },
        { id: 59, text: "Monitor inflammatory markers (hsCRP, IL-6)", impact: "Inflammation drives aging, track it" },
        { id: 60, text: "Regular cognitive testing/brain games", impact: "Early detection of cognitive decline" }
      ]
    },
    "TIER 6: ENVIRONMENTAL & TOXIN REDUCTION": {
      color: "from-pink-500 to-red-500",
      icon: "🌍",
      description: "Reduce your toxic burden. Small changes compound over decades.",
      actions: [
        { id: 61, text: "Filter drinking water (removes chlorine, fluoride, heavy metals)", impact: "Reduce daily toxin exposure" },
        { id: 62, text: "Use air purifier with HEPA filter (especially bedroom)", impact: "Reduce particulate matter, better sleep" },
        { id: 63, text: "Choose organic for 'Dirty Dozen' produce", impact: "Minimize pesticide exposure" },
        { id: 64, text: "Eliminate plastic food containers (use glass, steel)", impact: "Reduce endocrine disruptors (BPA, phthalates)" },
        { id: 65, text: "Choose non-toxic personal care products", impact: "Skin absorbs chemicals, choose clean ingredients" },
        { id: 66, text: "Reduce EMF exposure (airplane mode at night, distance from WiFi)", impact: "Potential cellular stress reduction" },
        { id: 67, text: "Avoid teflon/non-stick cookware (use cast iron, steel)", impact: "PFAS chemicals are 'forever chemicals'" },
        { id: 68, text: "Choose low-VOC paints and materials in home", impact: "Indoor air quality affects health daily" },
        { id: 69, text: "Minimize processed meat consumption", impact: "Nitrites, nitrates accelerate aging" },
        { id: 70, text: "Test home for mold, radon, lead", impact: "Hidden environmental hazards" }
      ]
    },
    "TIER 7: ADVANCED NUTRITION": {
      color: "from-green-600 to-teal-500",
      icon: "🥗",
      description: "Beyond basics. Nutrient timing, quality, and strategic choices.",
      actions: [
        { id: 71, text: "Eat 30+ different plant species weekly", impact: "Microbiome diversity = health diversity" },
        { id: 72, text: "Front-load protein intake (30-40g at breakfast)", impact: "Maintains muscle synthesis, satiety" },
        { id: 73, text: "Consume sulforaphane sources (broccoli sprouts)", impact: "Nrf2 activation, detoxification pathways" },
        { id: 74, text: "Include polyphenol-rich foods daily (berries, green tea)", impact: "Antioxidant, anti-inflammatory effects" },
        { id: 75, text: "Strategic fasting mimicking diet (quarterly 5-day)", impact: "Cellular cleanup, autophagy, stem cell regeneration" },
        { id: 76, text: "Optimize protein intake (1.6-2.2g per kg bodyweight)", impact: "Muscle preservation critical after 40" },
        { id: 77, text: "Include prebiotic fiber (10-15g daily)", impact: "Feeds beneficial gut bacteria" },
        { id: 78, text: "Consume nitrate-rich vegetables (beets, leafy greens)", impact: "Nitric oxide production, cardiovascular health" },
        { id: 79, text: "Limit advanced glycation end products (AGEs)", impact: "Reduce grilled/charred foods, skin aging" },
        { id: 80, text: "Strategic carb cycling (lower on rest days)", impact: "Metabolic flexibility, insulin sensitivity" }
      ]
    },
    "TIER 8: COGNITIVE & NEUROLOGICAL": {
      color: "from-teal-500 to-cyan-500",
      icon: "🧠",
      description: "Your brain is you. Protect it obsessively.",
      actions: [
        { id: 81, text: "Learn new language or musical instrument", impact: "Neuroplasticity, dementia prevention" },
        { id: 82, text: "Daily meditation practice (20+ minutes)", impact: "Slows brain aging, stress reduction" },
        { id: 83, text: "Protect hearing (limit loud noise exposure)", impact: "Hearing loss linked to cognitive decline" },
        { id: 84, text: "Optimize omega-3 to omega-6 ratio (aim 1:4 or better)", impact: "Brain structure, inflammation control" },
        { id: 85, text: "Practice dual n-back brain training", impact: "Working memory, fluid intelligence" },
        { id: 86, text: "Ensure adequate choline intake (eggs, liver)", impact: "Neurotransmitter synthesis, memory" },
        { id: 87, text: "Avoid anticholinergic medications when possible", impact: "Associated with dementia risk" },
        { id: 88, text: "Protect head from impacts (helmet sports)", impact: "TBI accelerates neurodegeneration" },
        { id: 89, text: "Maintain purpose and meaning in life", impact: "Ikigai linked to longevity in Blue Zones" },
        { id: 90, text: "Regular novel experiences and travel", impact: "Keeps brain adaptable, prevents rigidity" }
      ]
    },
    "TIER 9: HORMONAL OPTIMIZATION": {
      color: "from-cyan-500 to-blue-600",
      icon: "⚗️",
      description: "Hormones orchestrate aging. Keep them optimized.",
      actions: [
        { id: 91, text: "Monitor and optimize thyroid function", impact: "Master metabolic regulator" },
        { id: 92, text: "Manage cortisol through stress reduction", impact: "Chronic elevation accelerates aging" },
        { id: 93, text: "Optimize growth hormone (via sleep, exercise, fasting)", impact: "Muscle maintenance, cellular repair" },
        { id: 94, text: "Balance estrogen/progesterone (women, consult physician)", impact: "Bone health, cardiovascular protection" },
        { id: 95, text: "Support DHEA levels (supplement if low)", impact: "Master hormone, declines with age" },
        { id: 96, text: "Optimize insulin sensitivity (low-carb, exercise)", impact: "Metabolic health foundation" },
        { id: 97, text: "Monitor sex hormones annually after 40", impact: "Early intervention preserves function" },
        { id: 98, text: "Support melatonin production (dark sleep, limit screens)", impact: "Sleep quality, antioxidant effects" },
        { id: 99, text: "Consider peptide bioregulators for specific organs", impact: "Emerging longevity intervention" },
        { id: 100, text: "Stay informed on longevity research, adjust protocols", impact: "Field evolving rapidly, LEV requires staying current" }
      ]
    }
  };

  const totalActions = Object.values(actionTiers).reduce((sum, tier) => sum + tier.actions.length, 0);
  const completedCount = Object.keys(completedActions).filter(key => completedActions[key]).length;
  const completionPercent = Math.round((completedCount / totalActions) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-gray-100">
      {/* Animated background particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-30">
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.3) 0%, transparent 50%)`,
            transform: `translateY(${scrollY * 0.5}px)`
          }}
        />
        
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="mb-8 inline-block">
            <Dna className="w-20 h-20 text-purple-400 animate-spin" style={{ animationDuration: '20s' }} />
          </div>
          
          <h1 className="text-7xl md:text-9xl font-black mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 animate-pulse tracking-tight">
            LONGEVITY<br/>ESCAPE VELOCITY
          </h1>
          
          <p className="text-2xl md:text-3xl text-gray-300 mb-8 font-light tracking-wide">
            When medical progress outpaces aging
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="bg-gradient-to-br from-purple-900/50 to-purple-950/30 backdrop-blur-sm border border-purple-500/30 p-6 rounded-2xl">
              <Calendar className="w-12 h-12 text-purple-400 mb-4 mx-auto" />
              <div className="text-4xl font-bold text-purple-300 mb-2">2029-2035</div>
              <div className="text-sm text-gray-400">Predicted LEV Achievement<br/>(Kurzweil & de Grey)</div>
            </div>
            
            <div className="bg-gradient-to-br from-pink-900/50 to-pink-950/30 backdrop-blur-sm border border-pink-500/30 p-6 rounded-2xl">
              <Brain className="w-12 h-12 text-pink-400 mb-4 mx-auto" />
              <div className="text-4xl font-bold text-pink-300 mb-2">+1 Year</div>
              <div className="text-sm text-gray-400">Life expectancy gain<br/>per year lived</div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-900/50 to-blue-950/30 backdrop-blur-sm border border-blue-500/30 p-6 rounded-2xl">
              <Zap className="w-12 h-12 text-blue-400 mb-4 mx-auto" />
              <div className="text-4xl font-bold text-blue-300 mb-2">AI x BIO</div>
              <div className="text-sm text-gray-400">Exponential acceleration<br/>of discovery</div>
            </div>
          </div>

          <div className="flex gap-4 justify-center">
            <button
              onClick={() => document.getElementById('action-list').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all transform hover:scale-105 shadow-2xl shadow-purple-500/50"
            >
              Explore the 100 Actions
            </button>
            <button
              onClick={() => document.getElementById('ai-acceleration').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all transform hover:scale-105 shadow-2xl shadow-blue-500/50"
            >
              AI's Role
            </button>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-purple-400" />
        </div>
      </div>

      {/* Concept Explanation */}
      <div className="relative py-24 px-4 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold mb-12 text-center bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            The Concept
          </h2>
          
          <div className="space-y-8 text-lg text-gray-300 leading-relaxed">
            <p className="text-xl">
              <span className="text-purple-400 font-bold">Longevity Escape Velocity (LEV)</span> is the theoretical point where 
              medical advances extend your remaining life expectancy by more than one year for every year that passes.
            </p>
            
            <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 p-8 rounded-2xl border border-purple-500/30">
              <div className="text-center mb-6">
                <Activity className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-purple-300">The Physics Analogy</h3>
              </div>
              <p>
                Like a rocket achieving escape velocity to break free from Earth's gravity, your biological age would begin 
                moving <span className="text-pink-400 font-semibold">backwards in time</span> relative to medical progress. 
                You'd be aging slower than science is advancing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-900/20 border border-red-500/30 p-6 rounded-xl">
                <h4 className="text-xl font-bold text-red-400 mb-3">Today (2026)</h4>
                <p className="text-gray-400">
                  Each year of life uses up a year of longevity, but medical research gives back ~4 months.
                  <span className="block mt-2 text-red-300 font-semibold">Net loss: 8 months per year</span>
                </p>
              </div>
              
              <div className="bg-green-900/20 border border-green-500/30 p-6 rounded-xl">
                <h4 className="text-xl font-bold text-green-400 mb-3">At LEV (~2030s)</h4>
                <p className="text-gray-400">
                  Each year of life uses up a year, but medical advances give back 12+ months.
                  <span className="block mt-2 text-green-300 font-semibold">Net gain: Time runs backwards</span>
                </p>
              </div>
            </div>

            <p className="text-center text-xl text-purple-300 font-semibold py-8">
              "The first person to live to 1,000 years old may already be alive today — and they might be only a few years older than the first person to reach 150."
              <span className="block text-sm text-gray-400 mt-2">— Dr. Aubrey de Grey</span>
            </p>
          </div>
        </div>
      </div>

      {/* AI Acceleration Section */}
      <div id="ai-acceleration" className="relative py-24 px-4 bg-gradient-to-b from-slate-900 to-blue-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Sparkles className="w-16 h-16 text-blue-400 mx-auto mb-6 animate-pulse" />
            <h2 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-400">
              How AI is Accelerating LEV
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Artificial intelligence is compressing decades of biological research into months, fundamentally changing the timeline for longevity breakthroughs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 p-8 rounded-2xl border border-blue-500/30">
              <h3 className="text-2xl font-bold text-blue-300 mb-4 flex items-center gap-3">
                <LineChart className="w-8 h-8" />
                Drug Discovery Speed
              </h3>
              <div className="space-y-4 text-gray-300">
                <p><span className="text-red-400 font-bold">Traditional:</span> 10-15 years, $2.5B+ per drug, 90% failure rate</p>
                <p><span className="text-green-400 font-bold">AI-Powered:</span> 2-3 years, months to design, 70%+ success in early AI trials</p>
                <p className="text-sm text-blue-300 italic mt-4">
                  AlphaFold solved the protein folding problem in 2020. Moderna designed COVID vaccine in 2 days using AI.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 p-8 rounded-2xl border border-purple-500/30">
              <h3 className="text-2xl font-bold text-purple-300 mb-4 flex items-center gap-3">
                <Brain className="w-8 h-8" />
                Simulated Biology
              </h3>
              <div className="space-y-4 text-gray-300">
                <p>AI creates digital twins of biological systems to test interventions in silico before human trials</p>
                <p className="text-sm text-purple-300 italic">
                  Virtual clinical trials could compress safety/efficacy testing from years to hours while testing millions of virtual patients
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 p-8 rounded-2xl border border-cyan-500/30">
            <h3 className="text-2xl font-bold text-cyan-300 mb-6 text-center">Recent AI-Driven Breakthroughs (2024-2025)</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-2">🧬</div>
                <h4 className="font-bold text-cyan-300 mb-2">Ouabain Discovery</h4>
                <p className="text-sm text-gray-400">AI identified senolytic drug improving frailty biomarkers in mice (2025)</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2">💊</div>
                <h4 className="font-bold text-cyan-300 mb-2">Multi-Target Drugs</h4>
                <p className="text-sm text-gray-400">AI designing drugs hitting 3+ aging pathways simultaneously (Scripps 2025)</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-2">🔬</div>
                <h4 className="font-bold text-cyan-300 mb-2">Protein Design</h4>
                <p className="text-sm text-gray-400">Nobel Prize 2024 for AlphaFold — AI solving 50-year protein problem</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-2xl text-blue-300 font-semibold">
              "By 2029, you'll get back a full year. Past 2029, you'll get back more than a year. You'll go backwards in time."
              <span className="block text-sm text-gray-400 mt-2">— Ray Kurzweil, 2024</span>
            </p>
          </div>
        </div>
      </div>

      {/* Action List Section */}
      <div id="action-list" className="relative py-24 px-4 bg-gradient-to-b from-blue-950 to-slate-950">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-blue-400">
              Your LEV Roadmap
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              100 evidence-based actions, prioritized by impact and evidence strength
            </p>
            
            <div className="bg-gradient-to-r from-green-900/40 to-blue-900/40 p-6 rounded-2xl border border-green-500/30 inline-block">
              <div className="text-sm text-gray-400 mb-2">Your Progress</div>
              <div className="text-5xl font-bold text-green-400">{completionPercent}%</div>
              <div className="text-sm text-gray-400 mt-2">{completedCount} of {totalActions} completed</div>
              <div className="w-64 bg-gray-800 rounded-full h-3 mt-4">
                <div 
                  className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>
            </div>

            <p className="text-sm text-gray-400 mt-6 max-w-3xl mx-auto">
              <span className="text-yellow-400 font-semibold">Important:</span> This is a hierarchy. Complete higher tiers before moving down. 
              Taking supplements won't help if you're still smoking. Building a strong foundation is everything.
            </p>
          </div>

          <div className="space-y-6">
            {Object.entries(actionTiers).map(([tierName, tier]) => (
              <div key={tierName} className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-2xl border border-gray-700/50 overflow-hidden backdrop-blur-sm">
                <button
                  onClick={() => toggleCategory(tierName)}
                  className="w-full p-6 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{tier.icon}</div>
                    <div className="text-left">
                      <h3 className={`text-2xl font-bold bg-gradient-to-r ${tier.color} bg-clip-text text-transparent`}>
                        {tierName}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">{tier.description}</p>
                    </div>
                  </div>
                  <ChevronDown 
                    className={`w-6 h-6 text-gray-400 transition-transform ${expandedCategories[tierName] ? 'rotate-180' : ''}`} 
                  />
                </button>
                
                {expandedCategories[tierName] && (
                  <div className="px-6 pb-6 space-y-3">
                    {tier.actions.map((action) => (
                      <div 
                        key={action.id}
                        onClick={() => toggleAction(action.id)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          completedActions[action.id]
                            ? 'bg-green-900/20 border-green-500/50'
                            : 'bg-slate-800/50 border-gray-600/30 hover:border-gray-500/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-1 flex-shrink-0">
                            {completedActions[action.id] ? (
                              <Check className="w-5 h-5 text-green-400" />
                            ) : (
                              <div className="w-5 h-5 rounded border-2 border-gray-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <span className={`font-medium ${completedActions[action.id] ? 'text-green-300 line-through' : 'text-gray-200'}`}>
                                {action.id}. {action.text}
                              </span>
                            </div>
                            <p className="text-sm text-gray-400 mt-2 italic">
                              💡 {action.impact}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 bg-gradient-to-br from-purple-900/40 to-pink-900/40 p-8 rounded-2xl border border-purple-500/30 text-center">
            <h3 className="text-3xl font-bold text-purple-300 mb-4">The Bottom Line</h3>
            <div className="space-y-4 text-gray-300 text-lg max-w-3xl mx-auto">
              <p>
                <span className="text-pink-400 font-bold">LEV isn't guaranteed</span>, but the science is accelerating exponentially. 
                Those who optimize their health TODAY buy themselves time to benefit from tomorrow's breakthroughs.
              </p>
              <p>
                Focus on the fundamentals first. Master sleep, nutrition, exercise, and stress management. 
                <span className="text-purple-400 font-semibold"> These alone could add 10-20 quality years.</span>
              </p>
              <p className="text-xl text-purple-300 font-bold pt-4">
                "The goal isn't to live forever. It's to live long enough to live forever."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-950 py-12 px-4 border-t border-gray-800">
        <div className="max-w-4xl mx-auto text-center text-gray-500 text-sm space-y-4">
          <p>
            This website synthesizes research from Aubrey de Grey, Ray Kurzweil, and current longevity science. 
            Always consult healthcare professionals before making major health changes.
          </p>
          <p className="text-purple-400">
            Created January 2026 • Based on latest research and predictions
          </p>
          <div className="flex justify-center gap-6 text-xs">
            <span>Sources: NIH, Nature, Cell, LEV Foundation</span>
            <span>•</span>
            <span>Last updated: 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LEVWebsite;
